import { AddressMetadataType, type SafeAddressMetadata } from '@/types/enums'

export interface GqlMetadataType {
  type: AddressMetadataType
  metadata:
    | { __typename?: string }
    | {
        threshold: number
        owners: string[]
      }
}

export type ApiMetadataType =
  | {
      kind: 'safe'
      threshold: number
      owners: string[]
    }
  | {
      kind: 'aragon'
    }
  | {
      kind: 'risk'
    }

export type MetadataType = GqlMetadataType | ApiMetadataType
type MetadataKind = AddressMetadataType | ApiMetadataType['kind']

/**
 * Type guard to check if metadata is Safe wallet metadata.
 * @param metadata - Address metadata to check
 * @returns True if metadata is Safe wallet metadata
 */
const isSafeMetadata = (
  metadata: GqlMetadataType['metadata'] | ApiMetadataType
): metadata is SafeAddressMetadata | Extract<ApiMetadataType, { kind: 'safe' }> => {
  return 'threshold' in metadata && Array.isArray(metadata.owners)
}

const getMetadataKind = (metadata: MetadataType): MetadataKind =>
  'type' in metadata ? metadata.type : metadata.kind

const getMetadataPayload = (
  metadata: MetadataType
): GqlMetadataType['metadata'] | ApiMetadataType =>
  'type' in metadata ? metadata.metadata : metadata

/**
 * Extracts Safe wallet quorum information from metadata list.
 * @param metadataList - List of address metadata
 * @returns Quorum string (e.g., "2/3") or undefined if not a Safe
 */
export const getSafeQuorum = (metadataList: readonly MetadataType[]) => {
  const safeMetadata = metadataList.find(
    addressMetadata => getMetadataKind(addressMetadata) === AddressMetadataType.Safe
  )
  const payload = safeMetadata ? getMetadataPayload(safeMetadata) : undefined

  if (!payload || !isSafeMetadata(payload)) {
    return
  }

  return `${payload.threshold}/${payload.owners.length}`
}

export const hasAragonMetadata = (metadataList: readonly MetadataType[]) =>
  metadataList.some(
    addressMetadata => getMetadataKind(addressMetadata) === AddressMetadataType.Aragon
  )
